import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { toast } from '@/components/ui/sonner';

/** Sign out every signed-in user, including admins, after this much inactivity. */
export const IDLE_LOGOUT_MS = 15 * 60 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;

/**
 * Ends the browser session after 15 minutes with no pointer, keyboard, or scroll activity.
 * Server-side idle checks in auth-service remain the source of truth if this tab is closed.
 */
export const useIdleLogout = () => {
  const { user, signOut } = useAuth();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signingOutRef = useRef(false);

  useEffect(() => {
    if (!user) {
      signingOutRef.current = false;
      return;
    }

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleLogout = () => {
      clearTimer();
      timerRef.current = setTimeout(() => {
        if (signingOutRef.current) {
          return;
        }
        signingOutRef.current = true;
        toast.info('You were signed out after 15 minutes of inactivity.');
        void signOut();
      }, IDLE_LOGOUT_MS);
    };

    const onActivity = () => {
      if (signingOutRef.current) {
        return;
      }
      scheduleLogout();
    };

    scheduleLogout();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
    };
  }, [user, signOut]);
};
